import { useState, useEffect, useCallback } from 'react';
import { Product, ProductCategory } from '../types/product';
import { productService } from '../services/productService';

export const useProducts = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProducts = useCallback(async (filters?: {
    category?: ProductCategory;
    search?: string;
    organic?: boolean;
    minPrice?: number;
    maxPrice?: number;
    sortBy?: 'price-asc' | 'price-desc' | 'rating' | 'newest';
  }) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await productService.getAllProducts(filters);
      setProducts(data);
    } catch (err) {
      setError('Failed to load products');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchFeaturedProducts = useCallback(async () => {
    try {
      const data = await productService.getFeaturedProducts();
      setFeaturedProducts(data);
    } catch (err) {
      console.error('Failed to load featured products', err);
    }
  }, []);

  const searchProducts = useCallback(async (query: string) => {
    if (!query.trim()) {
      await fetchProducts();
      return;
    }
    setIsLoading(true);
    try {
      const data = await productService.searchProducts(query);
      setProducts(data);
    } catch (err) {
      setError('Search failed');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [fetchProducts]);

  const getProductById = useCallback((id: string): Product | null => {
    return productService.getProductById(id);
  }, []);

  useEffect(() => {
    fetchProducts();
    fetchFeaturedProducts();
  }, [fetchProducts, fetchFeaturedProducts]);

  return {
    products,
    featuredProducts,
    isLoading,
    error,
    fetchProducts,
    searchProducts,
    getProductById,
  };
};

